import { createFeatureSelector, createSelector } from '@ngrx/store';
import { AppFeatureKey } from '../app-feature/app-feature-key';
import { UserTypePermission } from '../permissions/Permissions';
import { AppContextState, featureKey } from './core.state';
import { Roles } from 'src/app/user-admin/roles/models/Roles';
import { getSortedSearchResultColumns } from '../user-prefs/user-prefs.utils';

export const selectAppContextState = createFeatureSelector<AppContextState>(featureKey);

export const selectIsLoading = createSelector(selectAppContextState, state => state.isLoading);

export const selectAppContext = createSelector(selectAppContextState, state => state?.appContext);

export const selectOAuthProperties = createSelector(selectAppContextState, state => state.oAuthProperties);

export const selectUserPrefs = createSelector(selectAppContext, state => state?.userPrefs);

export const selectUserPrefsNumberOfRows = createSelector(selectUserPrefs, state => state?.searchResultPerPage);

export const selectUserColumnPrefs = createSelector(selectUserPrefs, state => state?.searchResultColumns);

export const selectUserSearchTableCols = createSelector(selectUserColumnPrefs, userColumnsString =>
    userColumnsString ? getSortedSearchResultColumns(userColumnsString) : []
);

export const selectUserData = createSelector(selectAppContext, state => state?.user);

export const selectUserLanguage = createSelector(selectUserPrefs, selectUserData, (userPrefs, userData) => {
    return userPrefs?.language ?? userData?.defaultLanguage ?? navigator.language.substring(0, 2).toUpperCase();
});

export const selectSimpleUserData = createSelector(selectAppContext, state => state?.simpleUser);

export const selectUserPermissions = createSelector(selectAppContextState, selectUserData, (selector1, selector2) => {
    let permissions: string[] | undefined;
    if (selector2?.permissions !== undefined) {
        permissions = [...selector2?.permissions];
    }
    if (selector2?.basicRole?.roleKey === Roles.SUPPLIER) {
        permissions = [...selector2?.permissions, UserTypePermission.IS_SUPPLIER];
    } else if (selector2?.basicRole?.roleKey === Roles.BBA) {
        permissions = [...selector2?.permissions, UserTypePermission.IS_BBA];
    }
    return permissions;
});

export const selectUserRole = createSelector(selectUserData, userData => userData?.basicRole?.roleKey);

export const selectIsSupplier = createSelector(selectUserData, userData => userData?.basicRole?.roleKey === Roles.SUPPLIER);

export const selectIsSystemAdmin = createSelector(selectUserData, userData => userData?.basicRole?.roleKey === Roles.SYSTEM_ADMIN);

export const selectTooltips = createSelector(selectAppContextState, state => state.tooltips);

export const selectTooltipTextByKey = (key: string) =>
    createSelector(selectTooltips, tooltips => {
        const tooltip = tooltips?.find(obj => obj.key === key);
        return tooltip ? tooltip.text ?? '' : '';
    });

export const selectAppFeatures = createSelector(selectAppContextState, state => state?.appFeatures);

export const selectMyWorkplaceFeatures = createSelector(selectAppFeatures, state => {
    return {
        myWorkplace: state?.find(f => f.featureKey === AppFeatureKey.MyWorkplace),
        myWorkplaceExternal: state?.find(f => f.featureKey === AppFeatureKey.MyWorkplaceExternal)
    };
});

export const selectIsHeaderMigrated = createSelector(selectAppFeatures, state => {
    const headerFeature = state?.find(f => f.featureKey === AppFeatureKey.Header);
    return headerFeature?.enabled ?? false;
});

export const selectCurrentUserQNumber = createSelector(selectUserData, userData => userData?.qNumber);

export const selectAppContextDwsmConfig = createSelector(selectUserData, userData => userData?.dwsmConfig);

export const selectHasUserPopupPref = (popupKey: string) =>
    createSelector(selectUserPrefs, userPrefs => {
        return userPrefs?.userPopupPrefs?.some(pref => pref.popupKey === popupKey);
    });
